import {
  EBusinessType,
  IBusinessAuthInfo,
  ICompanyMergeInfo,
  IUpdateBusinessAuth,
  ECompanyApproveStatus, 
} from '@core/main/types/common';
import {
  EAgentAccountStatus,
  IAccountEnterpriseParams,
  IAccountEnterpriseResponse,
  ICompanyDraftParams,
  IDraftCompanyItem,
  IManagePartyItem,
  ITaxSourceApplyJoin,
  ITaxSourceItem,
  ITaxSourceQuotaDetail,
  ITaxSourceQuotaParams,
  IUpdateSeniorExecutiveNames,
} from '@core/main/types';
import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { getHostbaseUrl } from '@core/tools';

const hostUrl = getHostbaseUrl();

/**
 * 格式化企业数据，根据审核状态计算各操作按钮的显示状态
 * @param data 企业列表数据
 * @returns 格式化后的企业列表数据，包含各按钮显示标志
 */
function formatEnterpriseData(data: IAccountEnterpriseResponse[]) {
  // 新增审核中状态集合：待审核、审核中、复核中
  const pendingStatuses = [
    ECompanyApproveStatus.PENDING,
    ECompanyApproveStatus.REVIEWING,
    ECompanyApproveStatus.AUDITING,
  ];
  // 编辑审核中状态集合：编辑待审核、编辑审核中、编辑复核中
  const editPendingStatuses = [
    ECompanyApproveStatus.EDIT_PENDING,
    ECompanyApproveStatus.EDIT_REVIEWING,
    ECompanyApproveStatus.EDIT_AUDITING,
  ];

  return data?.map((item) => {
    // 判断是否为新增审核失败状态
    const isFailed = item.approveStatus === ECompanyApproveStatus.FAILED;
    // 判断是否为编辑审核失败状态
    const isEditFailed = item.approveStatus === ECompanyApproveStatus.EDIT_FAILED;
    // 判断是否为新增审核中状态
    const isPending = pendingStatuses.includes(item.approveStatus);
    // 判断是否为编辑审核中状态
    const isEditPending = editPendingStatuses.includes(item.approveStatus);
    // 判断是否为审核成功状态（非失败、非审核中）
    const isSuccess = !isFailed && !isEditFailed && !isPending && !isEditPending;

    return {
      ...item,
      // 驳回原因按钮：新增审核失败或编辑审核失败时显示
      showRejectReason: isFailed || isEditFailed ? 1 : 0,
      // 编辑查看按钮：编辑审核中或编辑审核失败时显示
      showEditView: isEditPending || isEditFailed ? 1 : 0,
      // 重新申请按钮：仅新增审核失败时显示
      showReapply: isFailed ? 1 : 0,
      // 启用按钮：审核成功且当前为停用状态时显示
      showEnable: isSuccess && item.enable === EAgentAccountStatus.Disabled ? 1 : 0,
      // 停用按钮：审核成功且当前为启用状态时显示
      showDisable: isSuccess && item.enable === EAgentAccountStatus.Enabled ? 1 : 0,
      // 编辑按钮：非新增审核失败且非新增审核中时显示
      showEdit: !isFailed && !isPending ? 1 : 0,
      // 修改权限/税源地入驻按钮：审核成功时显示
      showAuthAndTax: isSuccess ? 1 : 0,
    };
  });
}

/**
 * 账户中心 - 企业管理数据查询
 */
export async function queryAccountEnterprise(param: IAccountEnterpriseParams) {
  let data = [] as IAccountEnterpriseResponse[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as IAccountEnterpriseParams;

  try {
    const { code, context, message } = await Http.post<{ data: IAccountEnterpriseResponse[]; total: number }>(`${hostUrl}/side/company/page`, param);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IAccountEnterpriseResponse[]; total: number };

      data = formatEnterpriseData(items || []);
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 账户中心 - 企业管理 - 销售列表
 */
export async function queryBusinessUserList(agentId?: number) {
  let data = [] as IManagePartyItem[];

  try {
    const { code, context, message } = await Http.post<Array<IManagePartyItem>>(`${hostUrl}/iam/identity/agent/list`, { agentId });

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || [];

      data = res || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return { data };
}

/**
 * 账户中心 - 企业管理 - 启用、停用
 */
export async function changeEnterpriseActiveStatus(id: number) {
  try {
    const { code, message } = await Http.post<void>(`${hostUrl}/side/company/changeEnable`, { id });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}
/**
 * 账户中心 - 企业管理 - 导出
 */

export async function exportEnterpriseManage(params: IAccountEnterpriseParams) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/side/company/export`,
      params,
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 账户中心 - 企业管理  详情 - 修改手机号
 */
export async function updateCurEnterprisePhone(companyId: number, newPhone: string) {
  try {
    const { code, message } = await Http.post<{companyId: number; newPhone: string}>(`${hostUrl}/side/companyPay/updatePhone/noCode`, {
      companyId,
      newPhone,
    });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 企业管理  详情 - 重置密码
 */
export async function resetCurEnterprisePhone(companyId: number) {
  try {
    const { code, message } = await Http.post<{companyId: string }>(`${hostUrl}/side/companyPay/resetPassword/noCode`, { companyId });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 企业管理 - 新增/修改
 * @param type false: 新增  true: 修改
 */
export async function insertAccountCompany(param: ICompanyMergeInfo, type: boolean) {
  param = JSON.parse(JSON.stringify(param)) as ICompanyMergeInfo;

  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/side/company/${!type ? 'insert' : 'update'}`, param);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 企业管理 - 保存草稿
 * @param type false: 新增  true: 修改
 */
export async function saveDraftCompany(param: ICompanyMergeInfo, type: boolean) {
  param = JSON.parse(JSON.stringify(param)) as ICompanyMergeInfo;

  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/side/draft/company/${type ? 'update' : 'insert'}`, param);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 代理商、企业管理 获取修改权限数据
 */
export async function queryBusinessList(businessType: EBusinessType) {
  let data = [] as IBusinessAuthInfo[];

  try {
    const { code, context, message } = await Http.get<IBusinessAuthInfo[]>(`${hostUrl}/iam/business/list`, { businessType });

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || [] as IBusinessAuthInfo[];

      data = res || [] as IBusinessAuthInfo[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 账户中心 - 代理商、企业管理 查询上级代理商可以给各端分配的业务权限
 */
export async function queryBusinessByAgentList(businessType: EBusinessType, agentId: number) {
  let data = [] as IBusinessAuthInfo[];

  try {
    const { code, context, message } = await Http.get<IBusinessAuthInfo[]>(`${hostUrl}/iam/business/list-by-agent`, {
      businessType,
      agentId, 
    });

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || [] as IBusinessAuthInfo[];

      data = res || [] as IBusinessAuthInfo[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 账户中心 - 代理商、企业管理详情 查询代理商、企业自身拥有的业务权限
 * @param isAgent true 代理商 false 企业
 * @param id 企业id/代理商id
 */
export async function queryBusinessAgentSelf(id: number, isAgent: boolean) {
  let data = [] as IBusinessAuthInfo[];

  try {
    const parmas = isAgent ? { agentId: id } : { companyId: id };
    const { code, context, message } = await Http.get<IBusinessAuthInfo[]>(`${hostUrl}/iam/business/${isAgent ? 'agent' : 'company'}-self`, parmas);

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || [] as IBusinessAuthInfo[];

      data = res || [] as IBusinessAuthInfo[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 账户中心 - 企业管理 获取修改权限数据
 */
export async function queryBusinessCompany(companyId: number) {
  let data = [] as IBusinessAuthInfo[];

  try {
    const { code, context, message } = await Http.get<IBusinessAuthInfo[]>(`${hostUrl}/iam/business/company`, { companyId });

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || [] as IBusinessAuthInfo[];

      data = res || [] as IBusinessAuthInfo[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 账户中心 - 企业管理 修改权限数据
 */
export async function updateBuinessCompany(params: {companyId: number; businessList: IBusinessAuthInfo[]}) {
  params = JSON.parse(JSON.stringify(params)) as {companyId: number; businessList: IBusinessAuthInfo[]};

  try {
    const { code, message } = await Http.put<{companyId: number; businessList: IBusinessAuthInfo[]}>(`${hostUrl}/iam/business/company`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 企业管理 批量修改权限数据
 */
export async function updateBuinessCompanies(params: IUpdateBusinessAuth) {
  params = JSON.parse(JSON.stringify(params)) as IUpdateBusinessAuth;

  try {
    const { code, message } = await Http.put<IUpdateBusinessAuth>(`${hostUrl}/iam/business/companies`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 企业管理 - 税源地入驻列表
 */
export async function queryTaxSourceList(businessId: number) {
  let data = [] as ITaxSourceItem[];

  try {
    const { code, context, message } = await Http.post<ITaxSourceItem[]>(`${hostUrl}/side/tax/source/list`, { businessId });

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || [] as ITaxSourceItem[];

      data = res || [] as ITaxSourceItem[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 企业管理 - 税源地入驻查询税源地配额
 */
export async function queryTaxSourceQuota(params: ITaxSourceQuotaParams) {
  let data = {} as ITaxSourceQuotaDetail;

  try {
    const { code, context, message } = await Http.post<ITaxSourceQuotaDetail>(`${hostUrl}/side/tax/source/query-quota`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as ITaxSourceQuotaDetail;

      data = res || {} as ITaxSourceQuotaDetail;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 企业管理 - 税源地入驻申请/编辑
 */
export async function updateTaxSourceApply(params: ITaxSourceApplyJoin, isEdit: boolean) {

  try {
    const { code, message } = await Http.post(`${hostUrl}/side/tax/source/${isEdit ? 'edit' : 'apply'}`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 企业管理 - 编辑董监高
 */
export async function updateSeniorExecutiveNames(params: IUpdateSeniorExecutiveNames) {

  try {
    const { code, message } = await Http.post(`${hostUrl}/side/company/update-senior-executive`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 安全设置 - 开启短信验证
 */
export async function openSmsVerify(params: {companyId: number; verifyCode: string}) {
  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/side/companyPay/sms-verify/open`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 安全设置 - 关闭短信验证
 */
export async function closeSmsVerify(params: {companyId: number; verifyCode: string}) {
  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/side/companyPay/sms-verify/close`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 企业管理 - 草稿分页
 */
export async function queryCompanyDraft(param: ICompanyDraftParams) {
  let data = [] as IDraftCompanyItem[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as ICompanyDraftParams;

  try {
    const { code, context, message } = await Http.post<{ data: IDraftCompanyItem[]; total: number }>(`${hostUrl}/side/draft/company/page`, param);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IDraftCompanyItem[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 查询草稿企业明细信息
 * @param id 企业ID
 */
export async function queryDraftCompanyDetail(id?: number) {
  let data = {} as ICompanyMergeInfo;

  try {
    const { code, context, message } = await Http.post<any>(`${hostUrl}/side/draft/company/detail`, { id: id });

    if (code === EAxiosResponseCode.Succeed) {
      let res = context || {};

      data = res || {} as ICompanyMergeInfo;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 账户中心 - 企业管理 - 草稿 - 删除
 */
export async function deleteCompanyDraft(id: number) {
  try {
    const { code, message } = await Http.post<void>(`${hostUrl}/side/draft/company/del`, { id });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 上传企业入网表回显数据
 * @param fileUrl 文件地址
 * @param fileName 文件名
 * @returns 企业明细信息
 */
export async function querySettledDetail(fileUrl: string, fileName: string) {
  let data = {} as ICompanyMergeInfo;

  try {
    const { code, context, message } = await Http.post<any>(`${hostUrl}/side/company/company-application-form/parse`, {
      fileUrl,
      fileName, 
    });

    if (code === EAxiosResponseCode.Succeed) {
      let res = context || {};

      data = res || {} as ICompanyMergeInfo;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 获取当前是否为维护状态
 */
export async function queryMaintenanceStatus() {
  try {
    const { code, message } = await Http.post(`${hostUrl}/hainan/hn/maintenance/check`);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}